import type { AuthSession } from '@/types/api'

import { apiClient } from '@/lib/api/client'
import { authSessionSchema } from '@/lib/api/schemas'

export interface LoginInput {
  email: string
  password: string
}

export interface SignupInput {
  email: string
  password: string
  user_name: string
  company_name: string
  mission?: string | undefined
}

export interface RefreshInput {
  refreshToken: string
}

/**
 * `POST /auth/login` — troca credenciais por sessão completa (access + refresh).
 *
 * O `apiClient.requestValidated` lança `ApiError` com `status=401` se as
 * credenciais forem rejeitadas. A página `/login` traduz isso em mensagem
 * inline para o usuário.
 */
export function login(input: LoginInput): Promise<AuthSession> {
  return apiClient.requestValidated('/auth/login', authSessionSchema, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/**
 * `POST /auth/signup` — cria user + company + app_user atomicamente e
 * devolve sessão pronta (auto-confirma email via admin.create_user no
 * backend; sign_up tradicional foi descartado porque SMTP está bloqueado
 * por Cloudflare WARP — gap R3 do PMO doc 2026-05-17).
 *
 * Backend exige flag `SIGNUP_ENABLED=true` no .env do server. Se
 * desabilitado, lança ApiError com status 403 e detail "signup_disabled".
 *
 * Backend pode opcionalmente restringir por domínio via
 * `SIGNUP_ALLOWED_DOMAINS` (csv). Se domínio rejeitado: 403
 * "domain_not_allowed: <dominio>".
 */
export function signup(input: SignupInput): Promise<AuthSession> {
  return apiClient.requestValidated('/auth/signup', authSessionSchema, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/**
 * `POST /auth/refresh` — rotaciona a sessão. Servidor pode (e deve) emitir
 * um novo `refreshToken` a cada uso (rotação) — invalidando o anterior.
 *
 * Importante: este endpoint é chamado pelo interceptor de 401 do `ApiClient`
 * (não pelo AuthProvider direto). Se ele falhar, o interceptor dispara o
 * fluxo de logout.
 */
export function refresh(input: RefreshInput): Promise<AuthSession> {
  return apiClient.requestValidated('/auth/refresh', authSessionSchema, {
    method: 'POST',
    body: JSON.stringify(input),
  })
}

/**
 * `POST /auth/logout` — invalida o refresh token no servidor. Best-effort:
 * mesmo que falhe (ex.: usuário offline), o cliente limpa o storage local.
 */
export function logout(): Promise<void> {
  return apiClient.request('/auth/logout', { method: 'POST' })
}

